import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../lib/GlobalVar';
import {
    ConsultaAlertas, ResolveAlerta, ResolveTodosAlertas, ReabreAlerta,
    ConsultaPrefsAlertas, SalvaPrefsAlertas
} from '../lib/api';
import Swal from 'sweetalert2';
import Icon from './Icon';

// iter9: 5 abas potenciais. "todos" sempre vis vel. abas vis veis sao preferencia do usuario.
const ABAS_TODAS = [
    { id: 'todos',      label: 'Todos',     match: () => true },
    { id: 'err',        label: 'Erros',     match: a => a.severidade === 'err' },
    { id: 'warn',       label: 'Avisos',    match: a => a.severidade === 'warn' || a.severidade === 'run' },
    { id: 'info',       label: 'Info',      match: a => a.severidade === 'info' || a.severidade === 'ok' },
    { id: 'resolvidos', label: 'Resolvidos', match: () => true } // filtro do backend
];

const PREFS_DEFAULT = {
    tags_prioritarias: [],
    abas_visiveis: ['todos','err','warn','info','resolvidos']
};

export default function Alertas() {
    const { user } = useContext(GlobalContext);
    const navigate = useNavigate();
    const ehTI = !!user && (user.setor === 'TI' || user.cargo === 'Administrador');

    const [aba, setAba] = useState('todos');
    const [pendentes, setPendentes] = useState([]);
    const [resolvidos, setResolvidos] = useState([]);
    const [prefs, setPrefs] = useState(PREFS_DEFAULT);
    const [modalPrefs, setModalPrefs] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); reloadResolvidos(); carregaPrefs(); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (aba === 'resolvidos') reloadResolvidos(); }, [aba]);

    async function reload() {
        const resp = await ConsultaAlertas({});
        setPendentes(Array.isArray(resp) ? resp : []);
    }
    async function reloadResolvidos() {
        const resp = await ConsultaAlertas({ apenas_resolvidos: true });
        setResolvidos(Array.isArray(resp) ? resp : []);
    }
    async function carregaPrefs() {
        if (!user?.id) return;
        const r = await ConsultaPrefsAlertas({ solicitante_id: user.id, user_id: user.id });
        if (r && !r.Error) setPrefs({
            tags_prioritarias: r.tags_prioritarias || [],
            abas_visiveis: r.abas_visiveis && r.abas_visiveis.length ? r.abas_visiveis : PREFS_DEFAULT.abas_visiveis
        });
    }

    async function resolver(id) {
        const c = await Swal.fire({ icon: 'question', title: 'Resolver este alerta?', showCancelButton: true });
        if (!c.isConfirmed) return;
        const resp = await ResolveAlerta({ solicitante: user?.email, id });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        try { window.dispatchEvent(new Event('centralbabita:alertas-atualizados')); } catch (e) {}
        reload();
        reloadResolvidos();
    }
    async function reabrir(id) {
        const c = await Swal.fire({ icon: 'question', title: 'Reabrir este alerta?', text: 'Volta para a aba de pendentes.', showCancelButton: true });
        if (!c.isConfirmed) return;
        const resp = await ReabreAlerta({ solicitante: user?.email, id });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        try { window.dispatchEvent(new Event('centralbabita:alertas-atualizados')); } catch (e) {}
        reloadResolvidos();
        reload();
    }

    // Filtro por aba
    const fonteAtual = aba === 'resolvidos' ? resolvidos : pendentes;
    const abaDef = ABAS_TODAS.find(t => t.id === aba) || ABAS_TODAS[0];
    const visiveis = useMemo(() => {
        let arr = fonteAtual.filter(abaDef.match);
        // Prioridade: alertas com tag prioritaria primeiro
        if (prefs.tags_prioritarias.length) {
            const setTags = new Set(prefs.tags_prioritarias);
            arr = [...arr].sort((a, b) => {
                const pa = (a.tags || []).some(t => setTags.has(String(t).toLowerCase())) ? 0 : 1;
                const pb = (b.tags || []).some(t => setTags.has(String(t).toLowerCase())) ? 0 : 1;
                return pa - pb;
            });
        }
        return arr;
    }, [fonteAtual, abaDef, prefs.tags_prioritarias]);

    // Contadores por aba (so contam abas "filtro local"; resolvidos = total carregado)
    const counts = ABAS_TODAS.reduce((acc, t) => {
        if (t.id === 'resolvidos') acc[t.id] = resolvidos.length;
        else acc[t.id] = pendentes.filter(t.match).length;
        return acc;
    }, {});

    const abasMostradas = ABAS_TODAS.filter(t => prefs.abas_visiveis.includes(t.id) || t.id === 'todos');

    function temPrioridade(a) {
        if (!prefs.tags_prioritarias.length) return false;
        const setTags = new Set(prefs.tags_prioritarias);
        return (a.tags || []).some(t => setTags.has(String(t).toLowerCase()));
    }

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num">06</span> Centro de alertas</div>
                    <h1 className="page-title">Alertas</h1>
                    <p className="page-sub">Eventos que precisam de revisão humana. Críticos notificam Telegram via canal de plantão.</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn ghost" onClick={() => setModalPrefs(true)}>
                        <Icon name="settings" size={11}/> Preferências
                    </button>
                    {ehTI && (
                        <button className="btn ghost" onClick={() => navigate('/TI/Telegram')} title="Canais do Telegram (TI)">
                            Configurar canais
                        </button>
                    )}
                    <button className="btn" onClick={async () => {
                        const ids = pendentes.map(a => a.id);
                        if (!ids.length) return Swal.fire({ icon:'info', title:'Sem alertas pendentes' });
                        const c = await Swal.fire({ icon: 'question', title: `Marcar ${ids.length} alerta(s) como vistos?`, showCancelButton: true });
                        if (!c.isConfirmed) return;
                        const r = await ResolveTodosAlertas({ solicitante: user?.email, ids });
                        if (r.Error) return Swal.fire({ icon:'error', title:'Falhou', text: r.Error });
                        try { window.dispatchEvent(new Event('centralbabita:alertas-atualizados')); } catch (e) {}
                        Swal.fire({ icon:'success', title:`${r.count} marcados`, timer:1200, showConfirmButton:false });
                        reload();
                        reloadResolvidos();
                    }}>Marcar tudo visto</button>
                </div>
            </div>

            <div className="kpi-strip" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                <div className="kpi-cell"><div className="kpi-label">Total</div><div className="kpi-val">{counts.todos}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Erros</div><div className="kpi-val" style={{ color: 'var(--status-err)' }}>{counts.err}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Avisos</div><div className="kpi-val">{counts.warn}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Info</div><div className="kpi-val">{counts.info}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Resolvidos</div><div className="kpi-val" style={{ color: 'var(--ink-mute)' }}>{counts.resolvidos}</div></div>
            </div>

            <div className="tabs">
                {abasMostradas.map(t => (
                    <button key={t.id} className={`tab ${aba === t.id ? 'active' : ''}`} onClick={() => setAba(t.id)}>
                        {t.label.toUpperCase()} <span className="tab-count">{String(counts[t.id] || 0).padStart(2, '0')}</span>
                    </button>
                ))}
            </div>

            {visiveis.length === 0 && <div className="empty">
                {aba === 'resolvidos' ? 'Nenhum alerta resolvido ainda.' : 'Nenhum alerta nesta aba. Tudo tranquilo.'}
            </div>}

            <div className="routine-list">
                {visiveis.map(a => {
                    const prio = temPrioridade(a);
                    return (
                        <div key={a.id} className={`alert-card${prio ? ' prioritario' : ''}`} style={{ display: 'flex', alignItems: 'stretch', padding: 0 }}>
                            <div className={`bar-severity ${a.severidade || 'paused'}`}/>
                            <div style={{ flex: 1, padding: '14px 18px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    {prio && <span className="chip" style={{ background: 'var(--accent-soft, #fff5d6)', borderColor: 'var(--accent, #c89b2a)' }}>★ Prioritário</span>}
                                    <span className="mono" style={{ fontSize: 12 }}>{a.titulo || a.mensagem}</span>
                                    {aba === 'resolvidos' ? (
                                        <button className="btn ghost sm" style={{ marginLeft: 'auto' }} onClick={() => reabrir(a.id)}>
                                            <Icon name="history" size={11}/> Reabrir
                                        </button>
                                    ) : (
                                        <button className="btn ghost sm" style={{ marginLeft: 'auto' }} onClick={() => resolver(a.id)}>
                                            <Icon name="check" size={11}/> Resolver
                                        </button>
                                    )}
                                </div>
                                {(a.tags || []).length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                        {a.tags.map(t => <span className="chip" key={t}>{t}</span>)}
                                    </div>
                                )}
                                {a.detalhes && <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>{a.detalhes}</div>}
                                {(a.origem || a.usuario || a.ts) && (
                                    <div className="mono" style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ink-mute)' }}>
                                        {a.origem || '-'} · {a.usuario || '-'} · {a.ts || '-'}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {modalPrefs && (
                <PrefsModal
                    prefs={prefs}
                    onClose={() => setModalPrefs(false)}
                    onSaved={(p) => { setPrefs(p); setModalPrefs(false); if (!p.abas_visiveis.includes(aba)) setAba('todos'); }}
                    user={user}
                />
            )}
        </div>
    );
}

function PrefsModal({ prefs, onClose, onSaved, user }) {
    const [tags, setTags] = useState(prefs.tags_prioritarias);
    const [abas, setAbas] = useState(prefs.abas_visiveis);
    const [novaTag, setNovaTag] = useState('');

    function addTag() {
        const v = novaTag.trim().toLowerCase();
        if (!v) return;
        if (tags.includes(v)) return;
        setTags([...tags, v]);
        setNovaTag('');
    }
    function delTag(t) { setTags(tags.filter(x => x !== t)); }
    function toggleAba(id) {
        if (id === 'todos') return; // trava
        setAbas(abas.includes(id) ? abas.filter(x => x !== id) : [...abas, id]);
    }

    async function salvar() {
        const r = await SalvaPrefsAlertas({
            solicitante: user?.email,
            solicitante_id: user?.id,
            user_id: user?.id,
            tags_prioritarias: tags,
            abas_visiveis: abas
        });
        if (r.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: r.Error });
        Swal.fire({ icon: 'success', title: 'Preferências salvas', timer: 900, showConfirmButton: false });
        onSaved({ tags_prioritarias: r.tags_prioritarias || tags, abas_visiveis: r.abas_visiveis || abas });
    }

    return (
        <div className="modal-mask" onClick={onClose}>
            <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">Preferências · Alertas</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
                        Configurações pessoais salvas por usuário. TI/Administrador pode editar as suas próprias aqui.
                    </p>

                    <div className="eyebrow" style={{ marginTop: 6, marginBottom: 8 }}>Tags prioritárias</div>
                    <p className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                        Alertas com qualquer dessas tags aparecem no topo da lista com destaque.
                    </p>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <input className="input mono" value={novaTag} onChange={e => setNovaTag(e.target.value)}
                               placeholder="ex.: seguranca, financeiro, plantao"
                               onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}/>
                        <button className="btn sm" onClick={addTag}><Icon name="plus" size={11}/></button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 28 }}>
                        {tags.length === 0 && <span className="muted" style={{ fontSize: 12 }}>Nenhuma tag prioritária.</span>}
                        {tags.map(t => (
                            <span key={t} className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {t}
                                <button onClick={() => delTag(t)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex' }}>
                                    <Icon name="x" size={10}/>
                                </button>
                            </span>
                        ))}
                    </div>

                    <div className="eyebrow" style={{ marginTop: 20, marginBottom: 8 }}>Abas visíveis</div>
                    <p className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                        Esconda abas que não usa. A aba <b>Todos</b> sempre fica visível.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {ABAS_TODAS.map(t => (
                            <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: t.id === 'todos' ? 0.6 : 1 }}>
                                <input
                                    type="checkbox"
                                    checked={abas.includes(t.id)}
                                    disabled={t.id === 'todos'}
                                    onChange={() => toggleAba(t.id)}
                                />
                                {t.label} {t.id === 'todos' && <span className="muted" style={{ fontSize: 11 }}>(sempre visível)</span>}
                            </label>
                        ))}
                    </div>
                </div>
                <div className="modal-foot">
                    <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
                    <button className="btn sm" onClick={salvar}>Salvar preferências</button>
                </div>
            </div>
        </div>
    );
}
